import keyBy from 'lodash/keyBy';
import forEach from 'lodash/forEach';
import invert from 'lodash/invert';
import mapValues from 'lodash/mapValues';

import resourceId from '~/misc/resourceId';
import brokersDataPreview from './dots-preview';

const resource = resourceId('brokers-search-data', {});

const getName = (data, DOT, strict) => {
  const broker = data && data.byDOT[DOT];
  if (!broker) {
    return strict ? null : DOT;
  }
  return broker.generalName || broker.realName || broker.legalName;
};

const getDescName = (data, DOT) => {
  const broker = data && data.byDOT[DOT];
  if (!broker) {
    return null;
  }
  return broker.descName;
};

const doIndex = (storage, name, id) => {
  const parts = name.toLowerCase().split(/\s|\./);
  parts.forEach(part => {
    const length = Math.min(part.length, 3);
    let curIndex = storage;
    for (let i = 0; i < (length); i++) {
      const letter = part[i];
      if (!curIndex.letters) {
        curIndex.letters = {};
      }
      curIndex.letters[letter] = curIndex.letters[letter] || {matches: {}};
      let matches = curIndex.letters[letter].matches;
      matches[id] = true;

      curIndex = curIndex.letters[letter];
    }
  });

  return storage;
};

const matchPart = (storage, letters) => {
  let curIndex = storage;

  const length = Math.min(3, letters.length);

  for (let i = 0; i < length; i++) {
    const letter = letters[i];
    curIndex = curIndex && curIndex.letters[letter];
  }

  return curIndex && curIndex.matches;
};

const includesSimple = (name, part) => name && name.toLowerCase().includes(part);

const matchBroker = (data, query, limit) => {
  let storage = data.index;

  if (!query) {
    return {
      list: data.sorted.slice(0, limit),
    };
  }

  let matched = {};

  const parts = query.trim().toLowerCase().split(/\s|\./);

  parts.forEach(part => {
    const prematch = matchPart(storage, part);
    const filterFn = (DOT) => {
      if (DOT.includes(part)) {
        return true;
      }
      const broker = data.byDOT[DOT];
      if (!broker) {
        return false;
      }

      if (includesSimple(broker.generalName, part)) {
        return true;
      }

      if (includesSimple(broker.descName, part)) {
        return true;
      }

      return false;
    };

    const stepmatch = prematch && (part.length > 3 ? keyBy(Object.keys(prematch).filter(filterFn)) : prematch);

    if (stepmatch) {
      forEach(stepmatch, (value, key) => {
        matched[key] = matched[key] || 0;
        matched[key]++;
      });
    }
  });

  const list = Object.keys(matched).sort((itemOne, itemTwo) => {
    const result = matched[itemTwo] - matched[itemOne];
    if (result) {
      return result;
    }

    return data.orderIndex[itemOne] - data.orderIndex[itemTwo];
  });

  return {
    list: list.slice(0, limit),
    total: list.length,
  };
};

const simpleTitleR = /(co\.?)?(\.?)(\,|\.)?\s?((llc(\.)?)|(inc(\.)?))/i;
const simpleTitle = (text) =>
  text.toLowerCase().replace(simpleTitleR, '').replace(/\s?\&\s?/, ' and ').replace(/\.\s?/, '\. ').trim();


const selectNames = (item) => {
  const generalName = item.singlePretender || item.bestMatch;
  if (!generalName) {
    return {
      generalName: item.realName,
      descName: item.legalName || null,
    };
  }

  const simpleGeneral = simpleTitle(generalName);
  const descName = [item.realName, item.legalName]
    .filter(name => (name && simpleTitle(name) !== simpleGeneral)).join(' / ');

  return {
    generalName,
    descName: descName || null,
  };
};

const toAltLowerCase = (name) => name && name
  .split(' ')
  .map(part => part.length > 4 ? part.toLowerCase() : part)
  .join(' ');

const fetchData = () =>
  window.fetch('/assets/data/dots-all.json', {
    method: 'GET',
  }).then(response => response.json()).then(result => {
    const byDOT = mapValues(result.byDOT, (value) => ({
      ...value,
      ...mapValues(selectNames(value), toAltLowerCase),
    }));

    let index = {};
    result.sorted.forEach(DOT => {
      const generalName = byDOT[DOT].generalName;
      const descName = byDOT[DOT].descName;

      doIndex(index, generalName, DOT);
      if (descName) {
        doIndex(index, descName, DOT);
      }
      doIndex(index, DOT, DOT);
    });

    return {
      ...result,
      byDOT: byDOT,
      index,
      orderIndex: mapValues(invert(result.sorted), parseFloat),
    };
  });

let fetched;
const fetchBrokers = () => {
  if (!fetched) {
    fetched = fetchData();
    return fetched;
  }

  return fetched.catch(() => {
    fetched = fetchData();
    return fetched;
  });
};

const prefetch = () => fetchBrokers();


export {
  resource,
  getName as getBrokerName, getDescName as getBrokerDescName,
  matchBroker, prefetch, brokersDataPreview,
};



// WEBPACK FOOTER //
// ./src/misc/brokers.js